import { useQuery } from '@tanstack/react-query';

import { AxiosError, AxiosInstance } from 'axios';
import { DeviceGeneralStatistics, Histogram, QueryResult } from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';

export interface DeviceStatisticsQueryClient {
  getFlowStatistics: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<Histogram>;
  useFlowStatisticsData: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ) => QueryResult<Histogram>;
  getQFlowStatistics: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<Histogram>;
  useQFlowStatisticsData: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ) => QueryResult<Histogram>;
  getTimeHistogramStatistics: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<Histogram>;
  useTimeHistogramStatisticsData: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ) => QueryResult<Histogram>;
  getGeneralStatistics: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ) => Promise<DeviceGeneralStatistics>;
  useGeneralStatisticsData: (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<DeviceGeneralStatistics>
  ) => QueryResult<DeviceGeneralStatistics>;
}

export function createDeviceStatisticsQueryClient(
  axios: AxiosInstance
): DeviceStatisticsQueryClient {
  return new DeviceStatisticsQueryClientImp(axios);
}

class DeviceStatisticsQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public getFlowStatistics = async (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<Histogram> => {
    const { data } = await this.axios.get<Histogram>(
      `/api/devices/${deviceNumber}/${sensorIndex}/counters/statistics/flow`,
      {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          every,
        },
      }
    );
    return data;
  };

  public useFlowStatisticsData = (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ): QueryResult<Histogram> => {
    return useQuery<Histogram, AxiosError>(
      [
        QueryKeys.FlowStatistics,
        deviceNumber,
        sensorIndex,
        fromDate.toISOString(),
        toDate.toISOString(),
        every,
      ],
      () => this.getFlowStatistics(deviceNumber, sensorIndex, fromDate, toDate, every),
      options
    );
  };

  public getQFlowStatistics = async (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<Histogram> => {
    const { data } = await this.axios.get<Histogram>(
      `/api/devices/${deviceNumber}/${sensorIndex}/counters/statistics/qflow`,
      {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          every,
        },
      }
    );
    return data;
  };

  public useQFlowStatisticsData = (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ): QueryResult<Histogram> => {
    return useQuery<Histogram, AxiosError>(
      [
        QueryKeys.QFlowStatistics,
        deviceNumber,
        sensorIndex,
        fromDate.toISOString(),
        toDate.toISOString(),
        every,
      ],
      () => this.getQFlowStatistics(deviceNumber, sensorIndex, fromDate, toDate, every),
      options
    );
  };

  public getTimeHistogramStatistics = async (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<Histogram> => {
    const { data } = await this.axios.get<Histogram>(
      `/api/devices/${deviceNumber}/${sensorIndex}/counters/statistics/time`,
      {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          every,
        },
      }
    );
    return data;
  };

  public useTimeHistogramStatisticsData = (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<Histogram>
  ): QueryResult<Histogram> => {
    return useQuery<Histogram, AxiosError>(
      [
        QueryKeys.TimeHistogramStatistics,
        deviceNumber,
        sensorIndex,
        fromDate.toISOString(),
        toDate.toISOString(),
        every,
      ],
      () => this.getTimeHistogramStatistics(deviceNumber, sensorIndex, fromDate, toDate, every),
      options
    );
  };

  public getGeneralStatistics = async (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number
  ): Promise<DeviceGeneralStatistics> => {
    const { data } = await this.axios.get<DeviceGeneralStatistics>(
      `/api/v2/devices/${deviceNumber}/${sensorIndex}/readings/statistics/general`,
      {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          every,
        },
      }
    );
    return data;
  };

  public useGeneralStatisticsData = (
    deviceNumber: string,
    sensorIndex: number,
    fromDate: Date,
    toDate: Date,
    every: number,
    options?: QueryOptions<DeviceGeneralStatistics>
  ): QueryResult<DeviceGeneralStatistics> => {
    return useQuery<DeviceGeneralStatistics, AxiosError>(
      [
        QueryKeys.GeneralStatistics,
        deviceNumber,
        sensorIndex,
        fromDate.toISOString(),
        toDate.toISOString(),
        every,
      ],
      () => this.getGeneralStatistics(deviceNumber, sensorIndex, fromDate, toDate, every),
      options
    );
  };
}
